<template>
  <div class="barcode-scanner d-md-none">
    <!-- Boton: solo visible en mobile -->
    <button
      type="button"
      class="btn btn-primary barcode-scanner__btn"
      title="Escanear codigo con la camara"
      @click="openScanner">
      <i class="bi bi-camera"></i>
    </button>

    <!-- Modal self-contained (clases Bootstrap 4, sin depender del JS de modal) -->
    <div v-if="open" class="barcode-scanner__overlay" @click.self="closeScanner">
      <div class="modal-dialog modal-dialog-centered barcode-scanner__dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Escanear codigo de barra</h5>
            <button type="button" class="btn btn-outline-light btn-sm barcode-scanner__close" aria-label="Cerrar" @click="closeScanner">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="modal-body barcode-scanner__body">
            <div :id="containerId" class="barcode-scanner__video"></div>
            <p v-if="error" class="text-danger small mt-2 mb-0">{{ error }}</p>
            <p v-else class="text-muted small mt-2 mb-0">Apunta la camara al codigo de barra.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

export default {
  name: 'BarcodeScannerButton',
  data() {
    return {
      open: false,
      error: null,
      html5Qrcode: null,
      // Id unico por instancia: evita colisiones de getElementById si el
      // componente llegara a montarse mas de una vez en la misma pagina.
      containerId: 'barcode-scanner-container-' + this._uid
    };
  },
  methods: {
    async openScanner() {
      this.error = null;
      this.open = true;

      // Esperar a que el contenedor exista en el DOM antes de arrancar la camara
      await this.$nextTick();

      try {
        // Restringido a codigos de barra lineales (sin QR/2D): mejora la fiabilidad
        // y velocidad del 1D y evita falsos positivos.
        this.html5Qrcode = new Html5Qrcode(this.containerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E
          ]
        });
        // Elegir camara: en Samsung facingMode:"environment" suele caer en la gran
        // angular (no enfoca de cerca). Enumeramos e intentamos la principal trasera.
        // Si no se puede determinar, fallback seguro a facingMode environment.
        const cameraSource = await this.pickCamera();

        await this.html5Qrcode.start(
          cameraSource,
          {
            fps: 10,
            qrbox: { width: 250, height: 150 }
          },
          (decodedText, decodedResult) => {
            this.onDetected(decodedText, decodedResult);
          },
          () => { /* errores de frame (ningun codigo en el cuadro): se ignoran */ }
        );
      } catch (e) {
        this.error = 'No se pudo acceder a la camara: ' + (e && e.message ? e.message : e);
      }
    },
    async pickCamera() {
      const fallback = { facingMode: 'environment' };
      try {
        const devices = await Html5Qrcode.getCameras();
        // Log para identificar los labels reales del dispositivo (ej. Samsung S23+)
        // y poder afinar cual es la camara principal.
        console.log('[BarcodeScanner] camaras disponibles:', devices);
        const backCameras = (devices || []).filter(d => {
          const label = (d.label || '').toLowerCase();
          return label.includes('back') || label.includes('rear') || label.includes('environment');
        });

        if (backCameras.length === 0) {
          // Sin traseras identificadas por label: usar facingMode.
          return fallback;
        } else if (backCameras.length === 1) {
          // Una sola trasera: usarla.
          return backCameras[0].id;
        }
        // Varias traseras: preferir "main" o la indexada " 0" (camera2 ... 0),
        // sino la segunda (la 1ra suele ser la gran angular en Samsung).
        const main = backCameras.find(d => {
          const label = (d.label || '').toLowerCase();
          return label.includes('main') || label.includes(' 0');
        });
        return main ? main.id : backCameras[1].id;
      } catch (e) {
        // No se pudieron enumerar las camaras: usar el fallback.
        return fallback;
      }
    },
    onDetected(decodedText, decodedResult) {
      // stopCamera() pone html5Qrcode en null; si llega un segundo frame antes de
      // que la camara frene, este guard evita emitir dos veces.
      if (!this.html5Qrcode) return;

      // Feedback de lectura exitosa: vibracion (Android/Chrome; iOS la ignora) + beep.
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
      this.beep();
      // QR => codigo interno; cualquier 1D (EAN/Code128/UPC/etc.) => codigo de barra.
      const formatName = decodedResult && decodedResult.result && decodedResult.result.format
        ? decodedResult.result.format.formatName
        : null;
      const type = formatName === 'QR_CODE' ? 'qr' : 'barcode';
      this.$emit('scan', decodedText, type);
      this.closeScanner();
    },
    beep() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(1000, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.1);
        // Cerrar el contexto al terminar: cada escaneo crea uno y los navegadores
        // limitan la cantidad de AudioContext simultaneos (~6 en Chrome).
        oscillator.onended = () => ctx.close();
      } catch (e) {
        // Audio no disponible: el beep es opcional, se ignora.
      }
    },
    async closeScanner() {
      // Frenar la camara ANTES de quitar el contenedor del DOM (v-if).
      await this.stopCamera();
      this.open = false;
      this.error = null;
    },
    async stopCamera() {
      if (!this.html5Qrcode) return;
      // Capturar y limpiar la referencia primero para que onDetected no reentre.
      const instance = this.html5Qrcode;
      this.html5Qrcode = null;
      try {
        if (instance.isScanning) {
          await instance.stop();
        }
        instance.clear();
      } catch (e) {
        // Ignorar errores al detener/limpiar la camara.
      }
    }
  },
  beforeDestroy() {
    // Asegurar liberacion del recurso si el componente se desmonta con el modal abierto
    this.stopCamera();
  }
};
</script>

<style scoped>
.barcode-scanner__overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  padding: 1rem;
}

.barcode-scanner__dialog {
  width: 100%;
  max-width: 500px;
}

.barcode-scanner__video {
  width: 100%;
  max-height: 60vh;
  overflow: hidden;
  background: #000;
  border-radius: 0.25rem;
}

/* Boton de cierre: btn de Bootstrap, visible sobre el header oscuro del modal */
.barcode-scanner__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.barcode-scanner__close .bi {
  font-size: 1.25rem;
}
</style>
